import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import {
  useDataProvider,
  useStore,
  SnackbarApiErrorMessage,
  useSnackbar,
} from '@ui-framework/index';
import { Box, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { MaterialReactTable } from 'material-react-table';
import columns from './lib/columns';
import { getNotes } from './lib/lib';

const NotesList = forwardRef(({ noteModalRef, readOnlyRole }, ref) => {
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getValues } = useFormContext();
  const snackbar = useSnackbar();
  const theme = useTheme();

  const { get, remove, getApiUrl } = useDataProvider(provider);
  const store = useStore();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // table state
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const serviceJob = getValues();

  const params = useMemo(
    () => ({
      serviceJobId: serviceJob?.id,
      get,
      remove,
      getApiUrl,
      snackbar,
    }),
    [serviceJob?.id, get, remove, getApiUrl, snackbar],
  );

  const initialize = useCallback(async () => {
    setLoading(true);
    try {
      const notesResponse = await getNotes({
        params,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });
      if (notesResponse?.ok) {
        setData(notesResponse?.data?.data);
        setRowCount(notesResponse?.data?.total);
      } else {
        // TODO: make this error message inline
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={notesResponse?.errors} />,
        });
        setData([]);
        setRowCount(0);
      }
    } catch (err) {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={[{ message: err?.message }]} />,
      });
    }
    setLoading(false);
  }, [params, pagination.pageIndex, pagination.pageSize, snackbar]);

  useEffect(() => {
    initialize();
  }, [initialize, columnFilters, globalFilter, sorting]);

  useImperativeHandle(ref, () => ({
    initialize: () => {
      initialize();
    },
  }));

  const memoizedColumns = useMemo(
    () =>
      columns({
        readOnlyRole,
        params,
        initialize,
        setHighlightedRowId,
        noteModalRef,
      }),
    [readOnlyRole, params, initialize, noteModalRef],
  );

  return (
    <Box sx={{ mt: 1 }}>
      <MaterialReactTable
        localization={store.domain.locale.getLocaleForMuiTable()}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            backgroundColor:
              row.original.id === highlightedRowId
                ? theme.palette.error.light
                : 'inherit',
            transition: 'background-color 300ms ease',
          },
        })}
        columns={memoizedColumns}
        data={data || []}
        getRowId={row => row.id}
        initialState={{ showColumnFilters: false }}
        manualFiltering
        manualPagination
        onColumnFiltersChange={setColumnFilters}
        onGlobalFilterChange={setGlobalFilter}
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        rowCount={rowCount || 0}
        state={{
          columnFilters,
          globalFilter,
          pagination,
          sorting,
          isLoading: loading,
        }}
        muiTablePaperProps={{ elevation: 0 }}
        enableColumnFilters={false}
        enableTopToolbar={false}
        enableBottomToolbar={rowCount > 0}
        enableTableHead={false}
      />
    </Box>
  );
});

export default NotesList;
